/** The learner's notebook: a private, always-available scratchpad for jotting
 *  down grammar rules, words, and doubts while studying. Typed notes only for
 *  now (the client's decision — "Notebook first, typing is enough"). Every
 *  note belongs to the signed-in user; the backend enforces that. */
import { useEffect, useState } from "react";
import {
  createNote, deleteNote, listNotes, listSections, moveNote, Note, updateNote,
} from "../../api/notebook";
import { ApiError } from "../../api/http";
import { Button, Card, EmptyState, Spinner } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";
import { WritingPractice } from "./WritingPractice";
import { NoteEditor } from "./NoteEditor";
import { RichNote } from "./richText";

export function NotebookPage() {
  const t = useT();
  const { push } = useToast();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  // Two things a learner does with a blank page: keep notes, and practise
  // writing. Tabs rather than two menu entries — it is one notebook.
  const [tab, setTab] = useState<"notes" | "writing">("notes");
  // The learner's own sections — their "tabs". null is the default one,
  // which always exists and is where an unfiled note lives.
  const [sections, setSections] = useState<string[]>([]);
  const [section, setSection] = useState<string | null>(null);
  const [newSection, setNewSection] = useState("");

  /** A tab exists as soon as a note is filed under it, so "adding" one is
   *  just switching to it — no empty tabs to tidy up later. */
  /** File an existing note under a different tab. */
  const move = async (id: string, to: string | null) => {
    try {
      const updated = await moveNote(id, to);
      setNotes((all) => all.map((n) => (n.id === id ? updated : n)));
    } catch (err) {
      fail(err);
    }
  };

  const addSection = () => {
    const name = newSection.trim();
    if (!name) return;
    setSections((s2) => (s2.includes(name) ? s2 : [...s2, name].sort()));
    setSection(name);
    setNewSection("");
  };

  useEffect(() => {
    listNotes()
      .then(setNotes)
      .catch(() => undefined)
      .finally(() => setLoading(false));
    listSections().then(setSections).catch(() => undefined);
  }, []);

  // null = the default tab; a name = that tab. There is no "all" view: a
  // note the learner cannot see is a note they will write twice.
  const shown = notes.filter((n) => (n.section ?? null) === section);

  const fail = (err: unknown) =>
    push("error", err instanceof ApiError ? err.message : t("common.error.generic"));

  // The Add button is disabled while the draft is blank, so `content` is
  // always non-empty here.
  const add = async () => {
    setSaving(true);
    try {
      const note = await createNote(draft.trim(), section);
      setNotes((prev) => [note, ...prev]);
      setDraft("");
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (note: Note) => {
    setEditingId(note.id);
    setEditText(note.content);
  };

  const saveEdit = async (id: string) => {
    try {
      const updated = await updateNote(id, editText.trim());
      setNotes((prev) => prev.map((n) => (n.id === id ? updated : n)));
      setEditingId(null);
    } catch (err) {
      fail(err);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      fail(err);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{t("notebook.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("notebook.subtitle")}</p>
      </div>

      <div role="tablist" className="flex gap-1 border-b border-line">
        {(["notes", "writing"] as const).map((id) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${
              tab === id
                ? "border-brand text-brand-dark"
                : "border-transparent text-muted hover:text-ink"
            }`}
          >
            {t(id === "notes" ? "notebook.tabNotes" : "notebook.tabWriting")}
          </button>
        ))}
      </div>

      {tab === "writing" && <WritingPractice />}

      {tab === "notes" && (
        <>
      <Card>
        <div className="space-y-3">
          {/* The learner's own tabs. "All" is not a section — it is the
              way to see everything at once, which is what the notebook was
              before they had tabs at all. */}
          <div className="flex flex-wrap items-center gap-1">
            {[null, ...sections].map((s2) => (
              <button
                key={s2 ?? "__default"}
                onClick={() => setSection(s2)}
                className={`rounded-control px-3 py-1 text-xs font-semibold ${
                  section === s2 ? "bg-brand-soft text-brand-dark" : "text-muted hover:text-ink"
                }`}
              >
                {s2 ?? t("notebook.allNotes")}
              </button>
            ))}
            <input
              value={newSection}
              onChange={(e) => setNewSection(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addSection();
              }}
              placeholder={t("notebook.newSection")}
              aria-label={t("notebook.newSection")}
              className="w-32 rounded-control border border-line bg-surface px-2 py-1 text-xs text-ink"
            />
          </div>

          <NoteEditor
            value={draft}
            onChange={setDraft}
            label={t("notebook.placeholder")}
            placeholder={t("notebook.placeholder")}
          />
          <div className="flex justify-end">
            <Button onClick={add} loading={saving} disabled={!draft.trim()}>
              {t("notebook.add")}
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="grid place-items-center py-8">
          <Spinner />
        </div>
      ) : shown.length === 0 ? (
        <EmptyState message={t("notebook.empty")} />
      ) : (
        <ul className="space-y-3">
          {shown.map((note) => (
            <li key={note.id}>
              <Card>
                {editingId === note.id ? (
                  <div className="space-y-3">
                    <NoteEditor
                      value={editText}
                      onChange={setEditText}
                      label={t("notebook.edit")}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setEditingId(null)}>
                        {t("common.cancel")}
                      </Button>
                      <Button onClick={() => saveEdit(note.id)} disabled={!editText.trim()}>
                        {t("common.save")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <RichNote content={note.content} />
                      {note.source_path && (
                        <a
                          href={note.source_path}
                          className="mt-1 inline-block text-xs font-semibold text-brand-dark underline"
                        >
                          {note.source_label ?? t("notebook.source")}
                        </a>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button variant="ghost" onClick={() => beginEdit(note)}>
                        {t("notebook.edit")}
                      </Button>
                      <select
                        value={note.section ?? ""}
                        aria-label={t("notebook.moveTo")}
                        onChange={(e) => void move(note.id, e.target.value || null)}
                        className="rounded-control border border-line bg-surface px-2 py-1 text-xs text-muted"
                      >
                        <option value="">{t("notebook.allNotes")}</option>
                        {sections.map((s2) => (
                          <option key={s2} value={s2}>{s2}</option>
                        ))}
                      </select>
                      <Button variant="ghost" onClick={() => remove(note.id)}>
                        {t("notebook.delete")}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
        </>
      )}
    </div>
  );
}
